import { zodResolver } from '@hookform/resolvers/zod'
import { useMemo } from 'react'
import { useForm } from 'react-hook-form'
import toast from 'react-hot-toast'
import { z } from 'zod'
import { InputText } from '@/components/commons/input-text'
import { ButtonLegacy } from '@/components/ui/button-legacy'
import { RefreshCcw01Icon, XCloseIcon } from '@/components/ui/icons'
import { Separator } from '@/components/ui/separator'
import { Spinner } from '@/components/ui/spinner'
import { NeighborhoodSection } from '@/features/subgeo/components/neighborhood-section'
import { useUpdateSubgeo } from '@/features/subgeo/queries/use-update-subgeo'
import { useSubgeoStore } from '@/features/subgeo/store/subgeo-store'
import type { Subgeo } from '@/features/subgeo/types'
import { useTranslation } from '@/translations/i18n'

// Schema for the edit subgeo form
const EditSubgeoFormSchema = z.object({
	name: z.string().max(100, 'subgeo:name_too_long').optional()
})
type EditSubgeoForm = z.infer<typeof EditSubgeoFormSchema>

interface SubgeoEditFormProps {
	subgeo: Subgeo
	onCancel: () => void
}

export const SubgeoEditForm = ({ subgeo, onCancel }: SubgeoEditFormProps) => {
	const { t } = useTranslation()
	const {
		selectedNeighborhoods,
		clearSelectedNeighborhoods,
		neighborhoods,
		toggleNeighborhood,
		detachedNeighborhoods,
		detachNeighborhood,
		restoreNeighborhood,
		clearDetachedNeighborhoods
	} = useSubgeoStore()

	const {
		register,
		handleSubmit,
		formState: { errors }
	} = useForm({
		resolver: zodResolver(EditSubgeoFormSchema),
		defaultValues: {
			name: subgeo.name
		}
	})

	const { mutate: updateSubgeo, isPending } = useUpdateSubgeo(
		{ id: subgeo.id },
		{
			currentSubgeo: subgeo,
			selectedNeighborhoods,
			detachedNeighborhoods
		},
		{
			onSuccess: () => {
				toast.success(t('subgeo:update_subgeo_success'))
				clearSelectedNeighborhoods()
				clearDetachedNeighborhoods()
				onCancel()
			}
		}
	)

	const onSubmit = (data: EditSubgeoForm) => {
		updateSubgeo({
			...(data.name && { name: data.name })
		})
	}

	const handleDetachNeighborhood = (neighborhoodId: string) => {
		detachNeighborhood(neighborhoodId)
	}

	const handleRestoreNeighborhood = (neighborhoodId: string) => {
		restoreNeighborhood(neighborhoodId)
	}

	const handleCancel = () => {
		clearSelectedNeighborhoods()
		clearDetachedNeighborhoods()
		onCancel()
	}

	const currentNeighborhoods = useMemo(() => {
		return neighborhoods.filter(
			(n) =>
				subgeo.neighborhoodIds.includes(n.acp_id) &&
				!detachedNeighborhoods.has(n.acp_id)
		)
	}, [neighborhoods, subgeo, detachedNeighborhoods])

	const detachedNeighborhoodsList = useMemo(() => {
		return neighborhoods.filter(
			(n) =>
				detachedNeighborhoods.has(n.acp_id) &&
				subgeo.neighborhoodIds.includes(n.acp_id)
		)
	}, [neighborhoods, detachedNeighborhoods, subgeo])

	const newlySelectedNeighborhoods = useMemo(() => {
		return neighborhoods.filter(
			(n) =>
				selectedNeighborhoods.has(n.acp_id) &&
				!subgeo.neighborhoodIds.includes(n.acp_id)
		)
	}, [neighborhoods, selectedNeighborhoods, subgeo])

	return (
		<>
			<div className='w-full px-2.5 pt-2.5'>
				<InputText
					disabled={isPending}
					errorMessage={
						errors.name?.message
							? t(errors.name.message as 'subgeo:name_too_long')
							: undefined
					}
					inputSize='sm'
					placeholder={t('subgeo:subgeo_name_placeholder')}
					type='text'
					typeIcon={
						<div
							className='h-2.5 w-2.5 rounded'
							style={{
								backgroundColor: subgeo.color
							}}
						/>
					}
					{...register('name')}
				/>
			</div>

			<Separator orientation='horizontal' />

			<form
				className='flex min-h-0 w-full flex-1 flex-col gap-2'
				onSubmit={handleSubmit(onSubmit)}
			>
				<div className='flex w-full flex-1 flex-col gap-4 overflow-y-auto p-2.5 [&::-webkit-scrollbar-thumb]:rounded [&::-webkit-scrollbar-thumb]:bg-neutral-300 [&::-webkit-scrollbar]:w-1'>
					{newlySelectedNeighborhoods.length > 0 && (
						<NeighborhoodSection
							chipVariant='success'
							containerGap={1}
							IconComponent={XCloseIcon}
							infoText={t('subgeo:adding_neighborhoods', {
								count: newlySelectedNeighborhoods.length
							})}
							isActive={true}
							neighborhoods={newlySelectedNeighborhoods}
							onIconClick={toggleNeighborhood}
						/>
					)}

					{detachedNeighborhoodsList.length > 0 && (
						<NeighborhoodSection
							chipVariant='danger'
							IconComponent={RefreshCcw01Icon}
							infoText={t('subgeo:detached_neighborhoods', {
								count: detachedNeighborhoodsList.length
							})}
							infoVariant='danger'
							isActive={true}
							isClickable={true}
							neighborhoods={detachedNeighborhoodsList}
							onIconClick={handleRestoreNeighborhood}
						/>
					)}

					{currentNeighborhoods.length > 0 && (
						<NeighborhoodSection
							chipVariant='neutral'
							IconComponent={XCloseIcon}
							infoText={t('subgeo:current_neighborhoods', {
								count: currentNeighborhoods.length
							})}
							isActive={false}
							isClickable={true}
							neighborhoods={currentNeighborhoods}
							onIconClick={handleDetachNeighborhood}
						/>
					)}

					{currentNeighborhoods.length === 0 &&
						newlySelectedNeighborhoods.length === 0 && (
							<div className='font-medium text-[13px] text-neutral-600'>
								{t('subgeo:click_map_to_add_neighborhoods')}
							</div>
						)}
				</div>

				<div className='flex w-full shrink-0 justify-between border-neutral-200 border-t px-2.5 py-1.5'>
					<ButtonLegacy
						disabled={isPending}
						onClick={handleCancel}
						size='sm'
						type='button'
						variant='tertiary'
					>
						{t('common:cancel')}
					</ButtonLegacy>
					<ButtonLegacy
						disabled={isPending}
						size='sm'
						type='submit'
						variant='primarySuccess'
					>
						{t('subgeo:save_changes')}
						{isPending && <Spinner size={16} />}
					</ButtonLegacy>
				</div>
			</form>
		</>
	)
}
